import React, { useState, useEffect, useCallback, useRef } from "react";
import "./ExploreCategoriesSection.css";

import frame1 from "../assets/categories/Frame 1.png";
import frame2 from "../assets/categories/Frame 2.png";
import frame3 from "../assets/categories/Frame 3.png";
import frame4 from "../assets/categories/Frame 4.png";
import frame5 from "../assets/categories/Frame 5.png";
import frame6 from "../assets/categories/Frame 6.png";
import frame7 from "../assets/categories/Frame 7.png";
import frame8 from "../assets/categories/Frame 8.png";
import frame9 from "../assets/categories/Frame 9.png";
import frame10 from "../assets/categories/Frame 10.png";
import frame11 from "../assets/categories/Frame 11.png";
import frame12 from "../assets/categories/Frame 12.png";
import frame13 from "../assets/categories/Frame 13.png";

const categories = [
  {
    id: "primary-pre-primary",
    title: "Primary / Pre-Primary",
    imageSrc: frame12,
    imageAlt: "Primary and pre-primary student",
  },
  {
    id: "k12-grades",
    title: "K-12 Grades",
    imageSrc: frame1,
    imageAlt: "K-12 grades students",
  },
  {
    id: "languages",
    title: "Languages",
    imageSrc: frame2,
    imageAlt: "Language learning materials",
  },
  {
    id: "music",
    title: "Music",
    imageSrc: frame3,
    imageAlt: "Musical instruments",
  },
  {
    id: "art",
    title: "Art",
    imageSrc: frame4,
    imageAlt: "Art and creative activities",
  },
  {
    id: "singing",
    title: "Singing",
    imageSrc: frame5,
    imageAlt: "Singing and vocal practice",
  },
  {
    id: "yoga",
    title: "Yoga",
    imageSrc: frame6,
    imageAlt: "Yoga practice",
  },
  {
    id: "photography",
    title: "Photography",
    imageSrc: frame7,
    imageAlt: "Photography session",
  },
  {
    id: "dance",
    title: "Dance",
    imageSrc: frame8,
    imageAlt: "Dance performance",
  },
  {
    id: "competitive-exams",
    title: "Competitive Exams",
    imageSrc: frame9,
    imageAlt: "Competitive exam preparation",
  },
  {
    id: "others",
    title: "Others",
    imageSrc: frame13,
    imageAlt: "Other learning categories",
  },
  {
    id: "it-courses",
    title: "IT Courses",
    imageSrc: frame10,
    imageAlt: "IT and coding courses",
  },
  {
    id: "special-kids",
    title: "Special Kids",
    imageSrc: frame11,
    imageAlt: "Special Kids",
  },
];

const GAP = 24;
const AUTO_PLAY_MS = 3500;

function ExploreCategoriesSection() {
  const total = categories.length;
  const [currentIndex, setCurrentIndex] = useState(total);
  const viewportRef = useRef(null);
  const [slideWidth, setSlideWidth] = useState(272);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [currentTranslate, setCurrentTranslate] = useState(0);
  const [prevTranslate, setPrevTranslate] = useState(0);
  const animationRef = useRef(null);

  const gapToUse = window.innerWidth <= 768 ? 12 : 16;

  const updateWidth = useCallback(() => {
    if (viewportRef.current) {
      if (window.innerWidth <= 768) {
        setSlideWidth(147.52);
      } else {
        setSlideWidth(272);
      }
    }
  }, []);

  useEffect(() => {
    updateWidth();
    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, [updateWidth]);

  // Sync translation with currentIndex when not dragging
  useEffect(() => {
    if (!isDragging) {
      const newTranslate = -(currentIndex * (slideWidth + gapToUse));
      setCurrentTranslate(newTranslate);
      setPrevTranslate(newTranslate);
    }
  }, [currentIndex, slideWidth, gapToUse, isDragging]);

  const goNext = useCallback(() => {
    setCurrentIndex((prev) => prev + 1);
  }, []);

  const goPrev = useCallback(() => {
    setCurrentIndex((prev) => prev - 1);
  }, []);

  useEffect(() => {
    if (isDragging) return;
    const timer = setInterval(goNext, AUTO_PLAY_MS);
    return () => clearInterval(timer);
  }, [goNext, isDragging]);

  const onDragStart = (e) => {
    setIsDragging(true);
    const x = e.type.includes("mouse") ? e.pageX : e.touches[0].clientX;
    setStartX(x);
    if (animationRef.current) cancelAnimationFrame(animationRef.current);
  };

  const onDragMove = (e) => {
    if (!isDragging) return;
    const x = e.type.includes("mouse") ? e.pageX : e.touches[0].clientX;
    const currentX = x - startX;
    setCurrentTranslate(prevTranslate + currentX);
  };

  const [isTransitioning, setIsTransitioning] = useState(true);

  const onDragEnd = () => {
    setIsDragging(false);
    const movedBy = currentTranslate - prevTranslate;

    // If moved enough, change index
    if (movedBy < -100) {
      setCurrentIndex((prev) => prev + 1);
    } else if (movedBy > 100) {
      setCurrentIndex((prev) => prev - 1);
    } else {
      // Snap back
      const newTranslate = -(currentIndex * (slideWidth + gapToUse));
      setCurrentTranslate(newTranslate);
    }
    setPrevTranslate(-(currentIndex * (slideWidth + gapToUse)));
  };

  // Handle infinite jump
  useEffect(() => {
    if (currentIndex >= total * 2) {
      const timer = setTimeout(() => {
        setIsTransitioning(false);
        setCurrentIndex(currentIndex - total);
      }, 400); // match transition time
      return () => clearTimeout(timer);
    } else if (currentIndex < total) {
      const timer = setTimeout(() => {
        setIsTransitioning(false);
        setCurrentIndex(currentIndex + total);
      }, 400); // match transition time
      return () => clearTimeout(timer);
    } else {
      setIsTransitioning(true);
    }
  }, [currentIndex, total]);

  const offset = currentTranslate;
  const displayCategories = [...categories, ...categories, ...categories];

  return (
    <section
      className="explore-categories"
      aria-labelledby="explore-categories-heading"
    >
      <div className="explore-categories__container">
        <header className="explore-categories__header">
          <h2
            id="explore-categories-heading"
            className="explore-categories__title"
          >
            Explore{" "}
            <span className="explore-categories__title-highlight">
              Categories
            </span>
          </h2>
          <p className="explore-categories__subtitle">
            Tailored Categories to Meet Your Needs
          </p>
        </header>

        <div className="explore-categories__track">
          <button
            type="button"
            className="explore-categories__btn explore-categories__btn--prev"
            onClick={goPrev}
            aria-label="Previous categories"
          >
            ‹
          </button>

          <div 
            className="explore-categories__viewport" 
            ref={viewportRef}
            onMouseDown={onDragStart}
            onMouseMove={onDragMove}
            onMouseUp={onDragEnd}
            onMouseLeave={onDragEnd}
            onTouchStart={onDragStart}
            onTouchMove={onDragMove}
            onTouchEnd={onDragEnd}
          >
            <ul
              className={`explore-categories__slider ${isDragging ? "dragging" : ""} ${!isTransitioning ? "no-transition" : ""}`}
              aria-label="Tutoring categories"
              style={{ transform: `translateX(${offset}px)` }}
            >
              {displayCategories.map((category, index) => (
                <li 
                  key={`${category.id}-${index}`} 
                  className={`explore-categories__slide ${!isTransitioning ? "no-transition" : ""}`}
                  style={{ width: slideWidth }}
                >
                  <article className="explore-categories__card">
                    <div className="explore-categories__card-image-wrapper">
                      <img
                        src={category.imageSrc}
                        alt={category.imageAlt}
                        className="explore-categories__card-image"
                      />
                    </div>
                    <div className="explore-categories__card-footer">
                      <h3 className="explore-categories__card-title">
                        {category.title}
                      </h3>
                    </div>
                  </article>
                </li>
              ))}
            </ul>
          </div>

          <button
            type="button"
            className="explore-categories__btn explore-categories__btn--next"
            onClick={goNext}
            aria-label="Next categories"
          >
            ›
          </button>
        </div>
      </div>
    </section>
  );
}

export default ExploreCategoriesSection;
